// GlobalWebsiteSearch.js - Global Search Bar for Ownezzy Landing Page
// Features instant search with live dropdown, Top 10 suggestion chips, and highlighted 'Create Custom Website' button.

import React, { useState, useRef, useEffect } from 'react';
import { heroTemplates as indianTemplates, demoStoreUrl, templatesForVisitor } from './Hero';

const heroTemplates = templatesForVisitor(indianTemplates); // the international stores too, first outside India
import { TOP_10_SEARCH_SUGGESTIONS } from '../utils/categoriesData';
import { trackGetFreeClick } from '../utils/track';
import { openCustomWebsiteModal } from '../utils/customModal';
import './GlobalWebsiteSearch.css';

export default function GlobalWebsiteSearch({ onOpenCustomModal }) {
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const searchWrapperRef = useRef(null);

  // Close dropdown on click outside
  useEffect(() => {
    function handleClickOutside(e) {
      if (searchWrapperRef.current && !searchWrapperRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const cleanQ = query.toLowerCase().trim();

  // Filter templates
  const matchingTemplates = heroTemplates.filter((t) => {
    if (!cleanQ) return false;
    const haystack = `${t.name} ${t.label} ${t.note} ${t.business} ${t.pitch}`.toLowerCase();
    return haystack.includes(cleanQ);
  });

  const handleSelectSuggestion = (suggestion) => {
    // Navigate to /websites with query
    window.location.href = `/websites?q=${encodeURIComponent(suggestion.label)}`;
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (cleanQ) {
        window.location.href = `/websites?q=${encodeURIComponent(cleanQ)}`;
      }
    }
  };

  return (
    <section className="global-search-section" id="global-search" aria-label="Search Ready-Made Websites">
      <div className="container search-container">
        {/* Section Heading */}
        <div className="global-search-header text-center">
          <span className="search-kicker">✨ 100+ LIVE READY-MADE STORES &amp; CUSTOM WEBSITES</span>
          <h2>Search Website as per Your Business</h2>
          <p>
            Find the perfect automated online store for your niche, or build your own custom branded website in 60 seconds.
          </p>
        </div>

        {/* Search Bar + Custom Website Action Group */}
        <div className="search-bar-and-cta-wrapper" ref={searchWrapperRef}>
          <div className="global-search-input-group">
            <span className="search-glass-icon" aria-hidden="true">
              🔍
            </span>
            <input
              type="text"
              className="global-search-input"
              placeholder="Search website templates (e.g. clothing, phone cases, plants, gifts, footwear...)"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setIsOpen(true);
              }}
              onFocus={() => setIsOpen(true)}
              onKeyDown={handleKeyDown}
              aria-label="Search website templates"
            />
            {query && (
              <button
                type="button"
                className="search-clear-btn"
                onClick={() => {
                  setQuery('');
                  setIsOpen(false);
                }}
                aria-label="Clear search"
              >
                ✕
              </button>
            )}
            <button
              type="button"
              className="search-submit-btn"
              onClick={() => {
                if (cleanQ) window.location.href = `/websites?q=${encodeURIComponent(cleanQ)}`;
                else window.location.href = '/websites';
              }}
            >
              Search
            </button>
          </div>

          {/* Highlighted 'Create Your Own Custom' Button */}
          <button
            type="button"
            className="highlighted-custom-btn"
            onClick={() => (onOpenCustomModal ? onOpenCustomModal(cleanQ) : openCustomWebsiteModal(cleanQ))}
            title="Create a customized website for your exact business"
          >
            <span className="custom-sparkle-pulse">✨</span>
            <span className="custom-btn-text">
              <strong>Create Your Own Custom Website</strong>
              <small>If your business is not in the list →</small>
            </span>
          </button>

          {/* Live Search Results Dropdown */}
          {isOpen && cleanQ && (
            <div className="global-search-dropdown-panel">
              <div className="dropdown-panel-header">
                <span>Matching Website Templates ({matchingTemplates.length})</span>
                <a href={`/websites?q=${encodeURIComponent(cleanQ)}`} className="view-all-link">
                  View All in Catalog →
                </a>
              </div>

              {matchingTemplates.length > 0 ? (
                <div className="dropdown-results-list">
                  {matchingTemplates.slice(0, 5).map((t) => {
                    const demoUrl = demoStoreUrl(t);
                    return (
                      <div key={t.id} className="dropdown-result-card">
                        <div className="card-left">
                          <span className="card-icon">{t.icon}</span>
                          <div className="card-info">
                            <strong>{t.name}</strong>
                            <span className="card-label">{t.label}</span>
                            <span className="card-pitch">{t.pitch}</span>
                          </div>
                        </div>
                        <div className="card-actions">
                          {demoUrl && (
                            <a
                              href={demoUrl}
                              target="_blank"
                              rel="noreferrer"
                              className="btn-dropdown-demo"
                              onClick={trackGetFreeClick}
                            >
                              👀 Live Demo
                            </a>
                          )}
                          <a
                            href={demoUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="btn-dropdown-get"
                            onClick={trackGetFreeClick}
                          >
                            🚀 Get Website
                          </a>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="dropdown-no-results">
                  <p>
                    No pre-built template matches <strong>&quot;{query}&quot;</strong>.
                  </p>
                </div>
              )}

              {/* Custom Website Prompt in Dropdown */}
              <div className="dropdown-custom-prompt">
                <div className="prompt-text">
                  <span className="prompt-icon">💡</span>
                  <span>
                    Selling something unique? <strong>We build custom websites for ANY niche!</strong>
                  </span>
                </div>
                <button
                  type="button"
                  className="btn-dropdown-custom-cta"
                  onClick={() => {
                    setIsOpen(false);
                    if (onOpenCustomModal) onOpenCustomModal(cleanQ);
                    else openCustomWebsiteModal(cleanQ);
                  }}
                >
                  ✨ Build Custom Store for &quot;{query}&quot;
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Top 10 Suggestion Chips */}
        <div className="top-search-suggestions">
          <span className="suggestions-label">🔥 Top Suggestions:</span>
          <div className="suggestions-pills-row" role="group" aria-label="Popular website searches">
            {TOP_10_SEARCH_SUGGESTIONS.map((item) => (
              <button
                key={item.id}
                type="button"
                className="suggestion-pill-btn"
                onClick={() => handleSelectSuggestion(item)}
              >
                <span className="pill-icon">{item.icon}</span>
                <span className="pill-name">{item.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Browse All Link */}
        <div className="search-explore-footer text-center">
          <a href="/websites" className="explore-all-websites-link">
            <span>Explore all 100+ ready-to-use business websites with filters</span>
            <span className="arrow-sym">→</span>
          </a>
        </div>
      </div>
    </section>
  );
}
